import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Loader2, ImageOff } from 'lucide-react';
import { fetchBanners } from '@/service/api';
import { useAssetRenewStore } from '@/stores';
import { ASSET_FILE_EXTENSIONS, getFileExtension } from '@/utils/asset-file-types';
import PortfolioPreviewModal from './PortfolioPreviewModal';

export interface BannerCarouselProps {
  /** 轮播图类型（向后端 API GET /banners 传入 type 参数） */
  type?: string;
  /** 轮播图风格变体：'default' (标准淡入淡出), 'stack' (卡片堆叠模式)，默认 'default' */
  variant?: 'default' | 'stack';
  /** 是否开启卡片堆叠模式（快捷配置，效果等同于 variant="stack"） */
  stacked?: boolean;
  /** 是否开启自动播放，默认 true */
  autoplay?: boolean;
  /** 自动播放间隔毫秒数，默认 5000 */
  interval?: number;
  /** 容器高度，例如 '360px', '100%', 400 等，默认 '360px' */
  height?: string | number;
  /** 自定义宽高比，例如 '16/9', '21/9'，开启时 height 将按比率自适应 */
  aspectRatio?: string;
  /** 圆角样式，默认 true ('rounded-2xl')，传 false 则为无圆角 'rounded-none'，也可传入自定义圆角类名 */
  rounded?: boolean | string;
  /** 额外容器 class 类名 */
  className?: string;
  /** 是否显示底部指示器/圆点，默认 true */
  showIndicators?: boolean;
  /** 是否显示左右切换箭头，默认 true */
  showArrows?: boolean;
  /** 左右箭头显示模式：'hover' (悬停显现), 'always' (常显), 'never' (隐藏)，默认 'hover' */
  arrowPosition?: 'hover' | 'always' | 'never';
  /** 是否在鼠标悬停时开启图片微缩放/浮动动画，默认 true */
  hoverScale?: boolean;
  /** 点击轮播项回调函数 */
  onBannerClick?: (banner: Api.Banner.BannerResponse, index: number) => void;
  /** 备用/兜底数据（当 API 报错或返回空数组时生效） */
  fallbackList?: Api.Banner.BannerResponse[];
  /** 空状态提示文案 */
  emptyText?: string;
  /** 自定义单个 Banner 项渲染函数 */
  renderItem?: (banner: Api.Banner.BannerResponse, index: number) => React.ReactNode;
  /** 是否允许点击开启大图/视频预览弹窗，默认 false (无预览) */
  enablePreview?: boolean;
}

const EMPTY_FALLBACK_LIST: Api.Banner.BannerResponse[] = [];

/**
 * 判定 Banner 关联的 access_url / 资源是否属于视频类型
 */
const checkIsVideoUrl = (url?: string, item?: Api.Banner.BannerResponse): boolean => {
  if (item?.asset) {
    const mime = (item.asset.mime_type || '').toLowerCase();
    const fileType = (item.asset.file_type || '').toUpperCase();
    if (fileType === 'VIDEO' || mime.startsWith('video/')) {
      return true;
    }
    if (item.asset.file_name) {
      const ext = getFileExtension(item.asset.file_name);
      if (ASSET_FILE_EXTENSIONS.video.includes(ext)) {
        return true;
      }
    }
  }

  const targetUrl = url || item?.asset?.access_url || item?.access_url || '';
  if (targetUrl) {
    const cleanPath = targetUrl.split('?')[0].split('#')[0];
    const ext = getFileExtension(cleanPath);
    if (ASSET_FILE_EXTENSIONS.video.includes(ext)) {
      return true;
    }
  }

  return false;
};

/**
 * 轮播图视频组件：支持动态播放/暂停控制与画中画悬浮面板禁用
 */
const BannerVideo: React.FC<{
  src: string;
  isActive: boolean;
  hoverScale: boolean;
}> = ({ src, isActive, hoverScale }) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (isActive) {
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          // 捕获并忽略浏览器自动播放限制报错
        });
      }
    } else {
      video.pause();
    }
  }, [isActive]);

  return (
    <video
      ref={videoRef}
      src={src}
      autoPlay={isActive}
      loop
      muted
      playsInline
      disablePictureInPicture
      controlsList="nodownload nopictureinpicture noremoteplayback"
      className={`w-full h-full object-cover transition-transform duration-700 select-none ${hoverScale ? 'group-hover/slide:scale-105' : ''
        }`}
    />
  );
};

/**
 * 通用前台轮播图组件
 * 封装数据获取、自动切换、悬停暂停、加载/备用防破图降级处理，支持标准淡入淡出及堆叠卡片模式
 */
export const BannerCarousel: React.FC<BannerCarouselProps> = ({
  type,
  variant = 'default',
  stacked = false,
  autoplay = true,
  interval = 5000,
  height = '360px',
  aspectRatio,
  rounded = true,
  className = '',
  showIndicators = true,
  showArrows = true,
  arrowPosition = 'hover',
  hoverScale = true,
  onBannerClick,
  fallbackList = EMPTY_FALLBACK_LIST,
  emptyText = '暂无轮播内容',
  renderItem,
  enablePreview = false,
}) => {
  const navigate = useNavigate();
  const isStacked = variant === 'stack' || stacked;
  const roundedClass = typeof rounded === 'string'
    ? rounded
    : rounded
      ? 'rounded-2xl'
      : 'rounded-none';
  const [banners, setBanners] = useState<Api.Banner.BannerResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isHovered, setIsHovered] = useState<boolean>(false);

  // 预览弹窗 State
  const [previewOpen, setPreviewOpen] = useState<boolean>(false);
  const [previewItem, setPreviewItem] = useState<{
    url: string;
    title?: string;
    intro?: string;
    isVideo: boolean;
  } | null>(null);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fallbackRef = useRef(fallbackList);
  fallbackRef.current = fallbackList;

  // 接口获取轮播图数据
  useEffect(() => {
    let isMounted = true;

    const loadBanners = async () => {
      setLoading(true);
      setError(false);
      try {
        const res = await fetchBanners({ type });
        if (!isMounted) return;
        if (Array.isArray(res) && res.length > 0) {
          // 按 sort 升序排列
          const sorted = [...res].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));
          setBanners(sorted);
        } else {
          setBanners(fallbackRef.current);
        }
      } catch (err) {
        if (!isMounted) return;
        console.error('获取轮播图接口失败:', err);
        setError(true);
        setBanners(fallbackRef.current);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadBanners();

    return () => {
      isMounted = false;
    };
  }, [type]);

  const assetCache = useAssetRenewStore((state) => state.assetCache);

  // 轮播图列表加载成功后，预存凭证并向 Store 注册资源上下文，由 Store 自动定时预热续签
  useEffect(() => {
    if (!banners.length) return;

    const renewRequests: Api.Upload.RenewResourceAccessURLsItemRequest[] = [];
    const singleAssetIds: string[] = [];

    banners.forEach((b) => {
      const bannerId = String(b.id || '');
      const assetId = b.asset_id || b.asset?.id || (b.asset as any)?.asset_id;
      if (!assetId) return;

      const assetIdStr = String(assetId);
      const accessUrl = b.asset?.access_url || b.access_url;
      const accessExpires = b.asset?.access_expires;

      // 1. 优先将原接口（fetchBanners）刚拉取到的新鲜 access_url 全部预存入 Store 缓存
      if (accessUrl) {
        useAssetRenewStore.getState().setAssetCacheItem(assetIdStr, accessUrl, accessExpires);
      }

      // 2. 收集所有的业务资源上下文（向 Store 注册活动上下文以供后续后台 5 秒巡检与预热续期）
      if (bannerId) {
        renewRequests.push({
          resource_type: 'BANNER',
          resource_id: bannerId,
          asset_ids: [assetIdStr],
        });
      } else {
        singleAssetIds.push(assetIdStr);
      }
    });

    if (renewRequests.length) {
      useAssetRenewStore.getState().renewBatchResources(renewRequests).catch(() => { });
    }

    singleAssetIds.forEach((assetId) => {
      useAssetRenewStore.getState().renewSingleAsset(assetId, 'BANNER').catch(() => { });
    });
  }, [banners]);

  // 切换到下一张
  const handleNext = useCallback(() => {
    if (banners.length <= 1) return;
    setCurrentIndex((prev) => (prev + 1) % banners.length);
  }, [banners.length]);

  // 切换到上一张
  const handlePrev = useCallback(() => {
    if (banners.length <= 1) return;
    setCurrentIndex((prev) => (prev - 1 + banners.length) % banners.length);
  }, [banners.length]);

  // 自动播放定时器控制
  useEffect(() => {
    if (!autoplay || isHovered || banners.length <= 1) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }

    timerRef.current = setInterval(() => {
      handleNext();
    }, interval);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [autoplay, interval, isHovered, banners.length, handleNext]);

  // 重置 index 边界保护
  useEffect(() => {
    if (currentIndex >= banners.length && banners.length > 0) {
      setCurrentIndex(0);
    }
  }, [banners, currentIndex]);

  // 计算容器 height / aspectRatio 样式
  const containerStyle: React.CSSProperties = {
    ...(aspectRatio ? { aspectRatio } : { height: typeof height === 'number' ? `${height}px` : height }),
  };

  // 处理轮播卡片点击
  const handleItemClick = (banner: Api.Banner.BannerResponse, index: number) => {
    if (onBannerClick) {
      onBannerClick(banner, index);
    }

    if (enablePreview) {
      const assetId = banner.asset_id || banner.asset?.id || (banner.asset as any)?.asset_id;
      const mediaUrl =
        (assetId && assetCache[assetId]?.url) || banner.asset?.access_url || banner.access_url;
      if (mediaUrl) {
        const isVideo = checkIsVideoUrl(mediaUrl, banner);
        setPreviewItem({
          url: mediaUrl,
          title: banner.name,
          intro: banner.content,
          isVideo,
        });
        setPreviewOpen(true);
        return;
      }
    }

    if (!onBannerClick) {
      const targetUrl = banner.access_url;
      if (targetUrl && targetUrl !== banner.asset?.access_url) {
        if (/^https?:\/\//i.test(targetUrl)) {
          window.open(targetUrl, '_blank', 'noopener,noreferrer');
        } else {
          navigate(targetUrl);
        }
      }
    }
  };

  return (
    <>
      <div
        className={`group relative transition-all hover:!translate-y-0 [&:hover]:!transform-none ${isStacked ? 'overflow-visible' : `overflow-hidden ${roundedClass} bg-slate-100`
          } ${className}`}
        style={containerStyle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* 1. 加载中状态 */}
        {loading && (
          <div className={`absolute inset-0 z-20 flex flex-col items-center justify-center bg-gray-950/70 backdrop-blur-sm transition-opacity ${roundedClass}`}>
            <Loader2 className="w-8 h-8 text-emerald-400 animate-spin mb-2" />
            <span className="text-xs text-gray-400">正在加载轮播内容...</span>
          </div>
        )}

        {/* 2. 空状态 / 异常回退均为空 */}
        {!loading && banners.length === 0 && (
          <div className={`absolute inset-0 z-10 flex flex-col items-center justify-center bg-gray-900/40 text-gray-400 ${roundedClass}`}>
            <ImageOff className="w-10 h-10 mb-2 opacity-50 text-gray-500" />
            <span className="text-sm font-medium">{error ? '轮播图加载失败' : emptyText}</span>
          </div>
        )}

        {/* 3. 轮播列表 */}
        {banners.length > 0 && (
          <div className="relative w-full h-full">
            {banners.map((item, idx) => {
              const isActive = idx === currentIndex;
              const assetId = item.asset_id || item.asset?.id || (item.asset as any)?.asset_id;
              const imageUrl =
                (assetId && assetCache[assetId]?.url) || item.asset?.access_url || item.access_url;
              const isVideo = checkIsVideoUrl(imageUrl, item);
              const hasLink = Boolean(
                onBannerClick ||
                enablePreview ||
                (item.access_url && item.access_url !== item.asset?.access_url)
              );

              // 计算堆叠偏移位置 (环形距离)
              const diff = (idx - currentIndex + banners.length) % banners.length;

              let cardAnimationClass = '';
              if (isStacked && banners.length > 1) {
                if (diff === 0) {
                  // 最顶层正中卡片 (直立)
                  cardAnimationClass =
                    `z-30 opacity-100 scale-100 rotate-0 translate-x-0 translate-y-0 shadow-2xl ${roundedClass} overflow-hidden`;
                } else if (diff === 1) {
                  // 第二层卡片 (向右歪斜 rotate 6.5deg)
                  cardAnimationClass =
                    `z-20 opacity-90 scale-[0.96] rotate-[6.5deg] translate-x-3 -translate-y-1 shadow-xl pointer-events-none filter brightness-[0.88] ${roundedClass} overflow-hidden`;
                } else if (diff === 2 && banners.length > 2) {
                  // 第三层卡片 (向左歪斜 rotate -5deg，左侧第一层透出)
                  cardAnimationClass =
                    `z-10 opacity-75 scale-[0.92] rotate-[-5deg] -translate-x-3 -translate-y-2 shadow-lg pointer-events-none filter brightness-[0.75] ${roundedClass} overflow-hidden`;
                } else if (diff === 3 && banners.length > 3) {
                  // 第四层卡片 (向左较深歪斜 rotate -9.5deg，左侧第二层透出)
                  cardAnimationClass =
                    `z-0 opacity-55 scale-[0.88] rotate-[-9.5deg] -translate-x-6 -translate-y-3 shadow-md pointer-events-none filter brightness-[0.6] ${roundedClass} overflow-hidden`;
                } else {
                  // 深层卡片
                  cardAnimationClass =
                    `z-0 opacity-0 scale-75 rotate-[-12deg] -translate-x-8 -translate-y-4 pointer-events-none ${roundedClass} overflow-hidden`;
                }
              } else {
                cardAnimationClass = isActive
                  ? `opacity-100 scale-100 z-10 ${roundedClass} overflow-hidden`
                  : `opacity-0 scale-105 z-0 pointer-events-none ${roundedClass} overflow-hidden`;
              }

              return (
                <div
                  key={item.id || idx}
                  className={`absolute inset-0 transition-all duration-500 ease-out origin-bottom ${isStacked ? 'w-[88%] h-[92%] left-[6%] top-[4%]' : 'w-full h-full'
                    } ${cardAnimationClass}`}
                  onClick={() => (isActive || !isStacked) && handleItemClick(item, idx)}
                >
                  {renderItem ? (
                    renderItem(item, idx)
                  ) : (
                    <div className={`relative w-full h-full group/slide ${hasLink ? 'cursor-pointer' : 'cursor-default'}`}>
                      {/* 背景多媒体 (图片或视频) */}
                      {imageUrl ? (
                        isVideo ? (
                          <BannerVideo
                            src={imageUrl}
                            isActive={isActive}
                            hoverScale={hoverScale}
                          />
                        ) : (
                          <img
                            src={imageUrl}
                            alt={item.name || item.content || item.asset?.file_name || `Banner-${idx + 1}`}
                            draggable={false}
                            className={`w-full h-full object-cover transition-transform duration-700 select-none ${hoverScale ? 'group-hover/slide:scale-105' : ''
                              }`}
                          />
                        )
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-[#edb46c] via-[#6b7651] to-[#294b4d] flex items-center justify-center relative overflow-hidden">
                          <div className="absolute inset-0 bg-[radial-gradient(circle_at_72%_28%,rgba(255,219,131,0.85),transparent_22%)]" />
                        </div>
                      )}

                      {/* 标签 (tag) 风格继承 HeroSection 标杆卡片 */}
                      {item.tag && (
                        <div className="absolute top-2.5 left-2.5 z-10">
                          <span className="inline-block px-2.5 py-0.5 rounded-full text-[11px] font-medium text-white bg-[#f4a53b] shadow-md backdrop-blur-xs">
                            {item.tag}
                          </span>
                        </div>
                      )}

                      {/* 文案展示层 (name / content / icon) 风格继承 HeroSection 标杆卡片 */}
                      {(item.name || item.content) && (
                        <div className="absolute inset-x-0 bottom-0 z-10 flex items-center justify-between gap-2 p-3 md:p-4 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
                          <div className="min-w-0 flex-1">
                            {item.name && (
                              <strong className="block text-white font-bold text-sm md:text-base line-clamp-1 drop-shadow-xs">
                                {item.name}
                              </strong>
                            )}
                            {item.content && (
                              <p className={`text-white/85 font-normal text-xs md:text-sm line-clamp-1 drop-shadow-xs ${item.name ? 'mt-0.5' : ''
                                }`}>
                                {item.content}
                              </p>
                            )}
                          </div>
                          {/* {hasLink && (
                          <ChevronRight className="w-4 h-4 md:w-5 md:h-5 text-white/80 flex-shrink-0 group-hover/slide:translate-x-0.5 transition-transform" />
                        )} */}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* 4. 左右切换箭头 */}
        {showArrows && banners.length > 1 && arrowPosition !== 'never' && (
          <>
            <button
              type="button"
              aria-label="Previous Slide"
              onClick={(e) => {
                e.stopPropagation();
                handlePrev();
              }}
              className={`absolute left-3 top-1/2 -translate-y-1/2 z-40 p-2.5 rounded-full bg-gray-900/60 hover:bg-emerald-600 text-white/90 hover:text-white backdrop-blur-md border border-white/10 shadow-lg transition-all duration-300 active:scale-95 ${arrowPosition === 'hover'
                  ? `${isHovered
                    ? 'opacity-100 translate-x-0 pointer-events-auto'
                    : 'opacity-0 translate-x-[-8px] pointer-events-none'
                  } group-hover:opacity-100 group-hover:translate-x-0 group-hover:pointer-events-auto`
                  : 'opacity-100 pointer-events-auto'
                }`}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            <button
              type="button"
              aria-label="Next Slide"
              onClick={(e) => {
                e.stopPropagation();
                handleNext();
              }}
              className={`absolute right-3 top-1/2 -translate-y-1/2 z-40 p-2.5 rounded-full bg-gray-900/60 hover:bg-emerald-600 text-white/90 hover:text-white backdrop-blur-md border border-white/10 shadow-lg transition-all duration-300 active:scale-95 ${arrowPosition === 'hover'
                  ? `${isHovered
                    ? 'opacity-100 translate-x-0 pointer-events-auto'
                    : 'opacity-0 translate-x-[8px] pointer-events-none'
                  } group-hover:opacity-100 group-hover:translate-x-0 group-hover:pointer-events-auto`
                  : 'opacity-100 pointer-events-auto'
                }`}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </>
        )}

        {/* 5. 底部指示器 */}
        {showIndicators && banners.length > 1 && (
          <div
            className={`absolute bottom-3 left-1/2 -translate-x-1/2 z-40 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/30 backdrop-blur-md border border-white/10 transition-all duration-300 ${isHovered ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
              } group-hover:opacity-100 group-hover:pointer-events-auto`}
          >
            {banners.map((_, idx) => (
              <button
                key={idx}
                type="button"
                aria-label={`Go to slide ${idx + 1}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentIndex(idx);
                }}
                className={`h-1.5 rounded-full transition-all duration-300 ${idx === currentIndex ? 'w-6 bg-emerald-400' : 'w-1.5 bg-white/40 hover:bg-white/70'
                  }`}
              />
            ))}
          </div>
        )}
      </div>

      {/* 6. 大图/视频预览弹窗 */}
      {enablePreview && (
        <PortfolioPreviewModal
          open={previewOpen}
          mediaUrl={previewItem?.url}
          title={previewItem?.title || '轮播图预览'}
          intro={previewItem?.intro}
          isVideo={previewItem?.isVideo}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </>
  );
};

export default BannerCarousel;
